import { supabase } from './supabase';

// ─── Workspaces — topic/theme folders for organizing episodes across shows ──
// Independent of shows. An episode can belong to zero or one workspace.
// Seeded suggestions shown to new users in the picker (not persisted until
// chosen — they're just ideas).

// Short-form + Long-form lead the suggestion list as the two defaults
// every creator gets \u2014 short-form holds clips/reels and long-form holds
// full episodes. They sit above the topical seeds so brand-new users can
// one-tap into the canonical split before adding topic folders.
export const SUGGESTED_WORKSPACES = [
  { name: 'Short-form',       icon: '\u{1F3AC}' },
  { name: 'Long-form',        icon: '\u{1F3A7}' },
  { name: 'Relationships',    icon: '\u2764\uFE0F' },
  { name: 'Mindset',          icon: '\u{1F9E0}' },
  { name: 'Human Behavior',   icon: '\u{1F50D}' },
  { name: 'Money + Career',   icon: '\u{1F4B0}' },
  { name: 'Health + Body',    icon: '\u{1F4AA}' },
  { name: 'Creative Work',    icon: '\u{1F3A8}' },
];

export async function getWorkspaces(userId) {
  if (!supabase || !userId) return [];
  const { data, error } = await supabase
    .from('workspaces')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  if (error) { console.error('getWorkspaces error:', error); return []; }
  return data || [];
}

export async function createWorkspace(userId, { name, description = '', color = '#ffb101', icon = null, parentId = null }) {
  if (!supabase || !userId) throw new Error('Not authenticated');
  if (!name?.trim()) throw new Error('Workspace name required');
  const row = { user_id: userId, name: name.trim(), description, color, icon };
  if (parentId) row.parent_id = parentId;       // nested folder (migration 065)
  let { data, error } = await supabase.from('workspaces').insert(row).select().single();
  // Pre-065: parent_id column missing → retry as a top-level folder so the
  // create still succeeds (it just won't be nested until 065 runs).
  if (error && parentId && /parent_id|column/i.test(error.message || '')) {
    const { parent_id, ...flat } = row;          // eslint-disable-line no-unused-vars
    ({ data, error } = await supabase.from('workspaces').insert(flat).select().single());
  }
  if (error) { console.error('createWorkspace error:', error); throw error; }
  return data;
}

export async function updateWorkspace(workspaceId, userId, updates) {
  if (!supabase || !userId || !workspaceId) throw new Error('Missing ids');
  const { data, error } = await supabase
    .from('workspaces')
    .update(updates)
    .eq('id', workspaceId)
    .eq('user_id', userId)
    .select();
  if (error) { console.error('updateWorkspace error:', error); throw error; }
  if (!data || data.length === 0) throw new Error('Update blocked (permissions or missing row)');
  return data[0];
}

export async function deleteWorkspace(workspaceId, userId) {
  if (!supabase || !userId || !workspaceId) throw new Error('Missing ids');
  const { error } = await supabase
    .from('workspaces')
    .delete()
    .eq('id', workspaceId)
    .eq('user_id', userId);
  if (error) { console.error('deleteWorkspace error:', error); throw error; }
}

// Count unorganized episodes (no workspace_id). Used by the nudge banner
// to decide when to encourage the user to create workspaces.
export async function getUnorganizedCount(userId) {
  if (!supabase || !userId) return 0;
  const { count, error } = await supabase
    .from('episodes')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .is('workspace_id', null);
  if (error) { console.error('getUnorganizedCount error:', error); return 0; }
  return count || 0;
}
